import { Firebase } from '../providers';
import { SiteActions } from '../state';

export * from './StoryblokClient';
export * from './StoryblokForm';
export * from './StoryblokFlexibleComponent';

export type StoryblokModule<T> = Omit<T, '_uid' | 'component'> & {
    css: string;
};

export type StoryblokSection<T> = { sectionData: T };

export namespace Storyblok {
    export type ContentVersion = 'upscore' | 'pengestyrke' | 'formue';

    /** TODO: Add types, move it to proper namespace */
    export type Components = any;

    export namespace Field {
        export type Asset = {
            alt?: string;
            copyright?: string;
            fieldtype: 'asset';
            filename: string;
            id?: string;
            name?: string;
            title?: string;
        };
    }

    export namespace Content {
        export type ContentType =
            | 'advantages'
            | 'articles'
            | 'errors'
            | 'factorGroups'
            | 'footer'
            | 'header'
            | 'middlesteps'
            | 'offers'
            | 'pages'
            | 'strings';

        export interface Advantage {
            content: {
                informations: string;
                text: string;
            };
            slug: string;
        }

        export interface Article {
            content: {
                author: string;
                featuredImage: Storyblok.Field.Asset;
                content: string;
                excerpt: string;
                site_description: string;
                site_title: string;
                title: string;
            };
            first_published_at: string;
            id: number;
            published_at: string;
            slug: string;
        }

        export interface Error {
            content: {
                button_event:
                    | 'reload'
                    | 'sign_out'
                    | 'homepage_redirect'
                    | 'redirect';
                button_label: string;
                message: string;
                redirect_url: string;
            };
            slug: string;
        }

        export type ErrorType =
            | 'profile-deletion-reauth'
            | 'general'
            | 'missing-requestid'
            | 'activate-error'
            | 'messages-not-found'
            | 'altinn-failed'
            | 'not-found'
            | 'debt-registry-unauthorized'
            | 'debt-flow'
            | null;

        export interface FactorGroup {
            content: {
                buttonLabelNegative: string;
                buttonLabelPositive: string;
                factorItems: {
                    component: string;
                    description: string;
                    name: string;
                    type: 'negative' | 'positive';
                    _uid: string;
                }[];
                heading: string;
                _uid: string;
            };
            slug: string;
        }

        export interface Footer {
            renderSettings: Storyblok.Component.RenderSettings;
            components: Storyblok.Components;
        }

        export interface Header {
            content: {
                logo: string;
                mobile_cta_label: string;
                mobile_cta_url: string;
                navigationMenu: {
                    component: string;
                    label: string;
                    onClick: 'logout' | '';
                    url: string;
                    visibility: ['public'?, 'auth'?];
                    _uid: string;
                }[];
            };
        }

        export interface Middlestep {
            content: {
                primary_offers_button_label: string;
                secondary_offers_button_label: string;
                text: string;
            };
            slug: string;
        }

        export interface Offer {
            content: {
                logo: string;
                logoPadding: boolean;
                trustboxCode: string;
                content: string;
                contact_form_enabled: boolean;
                /** TODO: Refactor this to accept new form field */
                contactFormFields: {
                    component: string;
                    disabled: boolean;
                    label: string;
                    name: string;
                    pattern: string;
                    placeholder: string;
                    required: boolean;
                    ruleLabel: string;
                    type:
                        | 'text'
                        | 'email'
                        | 'textarea'
                        | 'password'
                        | 'tel'
                        | 'checkbox'
                        | 'number';
                    _uid: string;
                }[];
                contact_form_submitted_message: string;
                contactFormButtonLabel: string;
                calculator_enabled: boolean;
                calculator_components: [];
            };
            slug: string;
        }

        export interface Page {
            content: {
                hide_footer: boolean;
                page_layout:
                    | 'layout_default'
                    | 'layout_myscore'
                    | 'layout_profile';
                // TODO: Add types to every section
                sections: any[];
                site_description: string;
                site_title: string;
                space_from_header: boolean;
                white_header_desktop: boolean;
                __typename: string;
                _uid: string;
                calculateMinHeight: boolean;
                pageGuard: Storyblok.PageGuards.PageGuard[];
            };
            full_slug: string;
            is_startpage: boolean;
            slug: string;
        }

        export interface String {
            content: {
                strings: {
                    _uid: string;
                    name: string;
                    value: string;
                }[];
            };
            slug:
                | 'site-metadata'
                | 'general-strings'
                | 'blog-strings'
                | 'paths'
                | 'fun-facts'
                | 'my-score-string-variables'
                | 'form-strings'
                | 'contact-friend'
                | 'my-score-factors'
                | 'debt-strings';
        }
    }

    export namespace BlockComponent {
        export interface PageLocation {
            location: {
                cached_url: string;
                fieldtype: string;
                id: string;
                linktype: string;
                url: string;
            };
        }
    }

    export namespace Component {
        export interface About {
            _uid: string;
            component: 'About';
            bubbleContent: string;
            bubbleTitle: string;
            leftColumnContent: string;
            leftColumnTitle: string;
        }

        export interface SimpleTabs {
            _uid: string;
            component: 'SimpleTabs';
            heading: string;
            items: {
                name: string;
                icon: string;
                content: string;
            }[];
        }

        export interface YoutubeVideo {
            _uid: string;
            component: 'YoutubeVideo';
            videoUrl: string;
            disablePadding: boolean;
            content: string;
            heading: string;
        }

        export interface AltinnProcess {
            _uid: string;
            component: 'AltinnProcess';
            backgroundImage: Storyblok.Field.Asset;
            scoreTellerTitle: string;
            funFactsTitle: string;
            redirectPathOnSuccess: string;
            redirectPathOnFailure: string;
            altinnConfirmationFields: any;
            altinnConfirmationRegisterFields: any;
            altinnConfirmationResetFields: any;
            headingExistingUser: string;
            subheadingExistingUser: string;
            heading: string;
            subheading: string;
        }

        export interface AuthenticationMethod {
            _uid: string;
            component: 'AuthenticationMethod';
            logo: Storyblok.Field.Asset;
            title: string;
            description: string;
            authMethod: string;
        }

        export interface AuthenticationMethods {
            _uid: string;
            component: 'AuthenticationMethods';
            headingImage: Storyblok.Field.Asset;
            headingText?: string;
            subHeading: string;
            description: string;
            authenticationMethods: AuthenticationMethod[];
            loaderText: string;
            redirectPath: string;
        }

        export interface BackgroundImage {
            _uid: string;
            component: 'backgroundImage';
            image: Storyblok.Field.Asset;
            styles: string;
        }

        export interface BubbleForm {
            _uid: string;
            component: 'BubbleForm';
            backgroundImage: string;
            buttonLabel: string;
            description?: string;
            fields: InputField[];
            formType: Firebase.FormSubmitType;
            successMessage: string;
            title?: string;
        }

        export interface BubbleSection {
            _uid: string;
            component: 'BubbleSection';
            background_image: string;
            background_image_mobile: string;
            button_title: string;
            button_url: {
                cached_url: string;
                url: string;
            };
            description: string;
            section_style: string;
            title: string;
            tracking_event_action_name: string;
        }

        export interface InputField {
            _uid: string;
            component: 'inputField';
            disabled: boolean;
            label: string;
            name: string;
            ruleLabel: string;
            placeholder: string;
            type:
                | 'text'
                | 'email'
                | 'textarea'
                | 'password'
                | 'tel'
                | 'checkbox'
                | 'number'
                | 'file';
            required: boolean;
            pattern: string;
            variant: 'roundedWhite' | 'boxedBahamaBlue';
            renderSettings: Storyblok.Component.RenderSettings;
        }

        export interface CustomStyles {
            _uid: string;
            component: 'customStyles';
            items: {
                component: 'customStylesItem';
                _uid: string;
                rule: string;
                breakpoints:
                    | 'all'
                    | 'min_sm'
                    | 'max_sm'
                    | 'min_md'
                    | 'max_md'
                    | 'min_lg'
                    | 'max_lg'
                    | 'min_xl'
                    | 'max_xl'
                    | 'between_sm_md'
                    | 'between_sm_lg'
                    | 'between_sm_xl'
                    | 'between_md_lg'
                    | 'between_md_xl'
                    | 'between_lg_xl';
            }[];
        }

        export interface ContactForm {
            _uid: string;
            component: 'ContactForm';
            id: string;
            buttonLabel: string;
            email_field_label: string;
            termsAgreement: boolean;
            termsText: string;
            termsCheckboxText: string;
            stepTwoText: string;
            termsButtonLabel: string;
            errorMessage: string;
            fields: any[];
            headline: string;
            name_field_label: string;
            successMessage: string;
            textarea_label: string;
            backgroundColor: string;
            color: string;
            buttonVariant: any;
            investorForm: boolean;
        }

        export interface DebtAdvisorSelect {
            _uid: string;
            component: 'DebtAdvisorSelect';
            heading: string;
            buttonLabel: string;
            emptyClientsMessage: string;
        }


        export interface GetScoreSection {
            _uid: string;
            component: 'GetScoreSection';
            background_image: string;
            get_score_button_link: string;
            get_score_button_title: string;
            get_score_description: string;
            get_score_title: string;
            tracking_event_action_name: string;
        }

        export interface Intro {
            _uid: string;
            component: 'intro';
            background_image: Storyblok.Field.Asset;
            text: string;
            content: string;
            objectFit: string;
            objectPosition: string;
        }

        export interface ProfileForm {
            _uid: string;
            component: 'profile_form';
            fields: Storyblok.Component.InputField[];
            heading: string;
            responseError: string;
            responseSuccess: string;
            datasourcesHeading: string;
            prepopulateWithData: boolean;
            buttonSaveProfileLabel: string;
            heading__i18n__staging: string;
            buttonRemoveSourceLabel: string;
            buttonAbortDeletionLabel: string;
            deleteConfirmationHeading: string;
            buttonConfirmDeletionLabel: string;
            responseError__i18n__staging: string;
            buttonToggleDeleteProfileLabel: string;
            responseSuccess__i18n__staging: string;
            datasourcesHeading__i18n__staging: string;
            buttonSaveProfileLabel__i18n__staging: string;
            buttonRemoveSourceLabel__i18n__staging: string;
            buttonAbortDeletionLabel__i18n__staging: string;
            deleteConfirmationHeading__i18n__staging: string;
            buttonConfirmDeletionLabel__i18n__staging: string;
            buttonToggleDeleteProfileLabel__i18n__staging: string;
        }

        export interface ScoreAdvantages {
            _uid: string;
            component: 'score_advantages';
            id: string;
            headline: string;
            advantages: any[];
        }

        export interface ScoreFactors {
            _uid: '2a637e0d-e101-4ea6-afc1-0220cbdede2d';
            component: 'score_factors';
            button_label_negative: string;
            button_label_positive: string;
            id: string;
            section_heading: string;
            slider_heading_negative: string;
            slider_heading_positive: string;
            swipe_label: string;
            tell_friend_button: string;
        }

        export interface ScoreGraph {
            _uid: string;
            component: 'score_graph';
            button_label_negative: string;
            button_label_positive: string;
            id: string;
            title_negative: string;
            title_positive: string;
        }

        export interface ScoreSources {
            _uid: string;
            component: 'score_sources';
            id: string;
            headline: string;
            sources: Source[];
            content: string;
            temp_info: string;
        }

        export interface Source {
            label: string;
            color: string;
            icon: string;
            event: 'temporaryMessage' | 'redirect';
            redirectUri: string;
        }

        export interface ScoreSummary {
            _uid: string;
            component: 'score_summary';
            id: string;
            headlines: ScoreSummaryHeadline[];
            comparisions: ScoreSummaryComparision[];
            button_compare: string;
            bubble_content: string;
        }

        export interface ScoreSummaryHeadline {
            _uid: string;
            component: 'score_summary_headline';
            text: string;
            name: string;
        }

        export interface ScoreSummaryComparision {
            _uid: string;
            component: 'score_summary_comparision';
            text: string;
            name: string;
        }

        export interface VideoSection {
            autoplay: boolean;
            component: string;
            redirect_on_click: string;
            video_poster: string;
            video_src_fullhd: string;
            video_src_hd: string;
            video_src_mobile: string;
            video_src_sd: string;
        }

        export interface Module {
            _uid: string;
            component: 'module';
            renderEffect: 'debtcollectionVerify';
            renderSettings: Storyblok.Component.RenderSettings;
            components: Storyblok.Components;
        }

        export interface Props {
            component: 'props';
            _uid: string;
            items: {
                component: 'propsItem';
                _uid: string;
                name: string;
                value: string;
            }[];
        }

        export type RenderSettings = [
            Storyblok.Component.BackgroundImage,
            Storyblok.Component.CustomStyles,
            Storyblok.Component.Props,
        ];
    }

    export type PageGuards<T extends Storyblok.PageGuards.PageGuard> = T & {
        /**
         * Sets currently resolved guard as `_loaded`.
         */
        setGuardLoaded: PageGuards.SetGuardLoaded;
    };

    export namespace PageGuards {
        export type PageGuard =
            | BankVerification
            | GetDebtCollectionClients
            | DebtCollectionExtract
            | GetDebtUnhandledCollectorDocuments
            | DebtUserSignedConsent
            | DebtUserActivated;

        export type SetGuardLoaded = () => Extract<
            SiteActions,
            { type: 'UPDATE_PAGEGUARDS' }
        >;

        export interface BankVerification {
            _uid: string;
            component: 'BankVerification';
            redirectAuthenticated: string;
            redirectNonAuthenticated: string;
        }

        export interface GetDebtCollectionClients {
            _uid: string;
            component: 'GetDebtCollectionClients';
        }

        export interface DebtCollectionExtract {
            _uid: string;
            component: 'DebtCollectionExtract';
        }

        export interface GetDebtUnhandledCollectorDocuments {
            _uid: string;
            component: 'GetDebtUnhandledCollectorDocuments';
        }

        export interface DebtUserSignedConsent {
            _uid: string;
            component: 'DebtUserSignedConsent';
            redirectSigned: string;
            redirectNonSigned: string;
        }

        export interface DebtUserActivated {
            _uid: string;
            component: 'DebtUserActivated';
            redirectActivated: string;
            redirectNonActivated: string;
        }
    }

    export namespace Form {
        export interface FormField {
            _uid: string;
            component: 'FormField';
            renderSettings: Storyblok.Component.RenderSettings;
            name: string;
            type:
                | 'text'
                | 'email'
                | 'textarea'
                | 'password'
                | 'tel'
                | 'checkbox'
                | 'number'
                | 'file'
                | 'hidden';
            as: 'input' | 'textarea';
            label?: string;
            initialValue?: string;
        }

        export interface DebtAdminProcessRequest {
            _uid: string;
            component: 'DebtAdminProcessRequest';
            collectorSelectPrefill: {
                name: string;
                accountNumber: string;
            }[];
            currentInterestRatePrefill: string;
            fields: FormField[];
            buttonSubmitLabel: string;
            buttonSaveAndArchiveLabel: string;
            buttonSaveAndRegisterLabel: string;
            successMessage: string;
        }
    }
}
